// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Per-cylinder detail card + table for the engine panel:
// - cylinderDetailHtml: the article shown above the table when a cylinder
//   is selected (CHT/EGT P95 stats, peer gap, probe confidence, flags).
// - renderCylinderDetailSection: the full panel — averages, detail card,
//   per-cylinder row table grouped by engine, with click/keyboard wiring
//   into setCylinderFocus.

export function createCylinderDetailRenderer({
  escapeHtml,
  scoreClass,
  cylinderScoreMarkup,
  currentEngineDescriptors,
  engineInfoForCylinderNumber,
  engineScopedProbeLabel,
  enginePeerKeysForCylinderNumber,
  cylinderMetricSummary,
  cylinderSensorSummary,
  cylinderProbeNote,
  engineWorkPoint,
  engineScopeCylinderNumbers,
  engineScopeVisibleEngines,
  engineSourceNumbers,
  visibleDivergenceCylinders,
  cylinderAverageForKeys,
  cylinderSpreadAverageForKeys,
  healthSummaryForCurrentEngineScope,
  setCylinderFocus,
  clearCylinderFocus,
  finiteValues,
  peerAverage,
  panelNumber,
  signedPanelNumber,
  quantile,
  extentFromValues,
  getFlightData,
  getSelectedCylinderNumber,
  setSelectedCylinderNumber,
}) {
  function cylinderDetailHtml(number, analysisRows, summary, engines = currentEngineDescriptors()) {
    const chtKey = `cht${number}`;
    const egtKey = `egt${number}`;
    const info = engineInfoForCylinderNumber(number, engines);
    const egtLabel = engineScopedProbeLabel(egtKey, engines) || egtKey.toUpperCase();
    const chtLabel = engineScopedProbeLabel(chtKey, engines) || chtKey.toUpperCase();
    const chtPeerKeys = enginePeerKeysForCylinderNumber(number, engines, "cht");
    const egtPeerKeys = enginePeerKeysForCylinderNumber(number, engines, "egt");
    const metric = cylinderMetricSummary(number, summary);
    const chtValues = finiteValues(analysisRows, chtKey);
    const egtValues = finiteValues(analysisRows, egtKey);
    const chtDeltaValues = analysisRows.map((point) => {
      const value = point[chtKey];
      const peer = peerAverage(point, chtPeerKeys, chtKey);
      return Number.isFinite(value) && Number.isFinite(peer) ? value - peer : null;
    }).filter(Number.isFinite);
    const egtDeltaValues = analysisRows.map((point) => {
      const value = point[egtKey];
      const peer = peerAverage(point, egtPeerKeys, egtKey);
      return Number.isFinite(value) && Number.isFinite(peer) ? value - peer : null;
    }).filter(Number.isFinite);
    const egtSensor = cylinderSensorSummary(summary, egtKey);
    const chtSensor = cylinderSensorSummary(summary, chtKey);
    const egtClass = egtSensor?.suspicion >= 24 ? "red" : egtSensor?.suspicion >= 10 ? "amber" : "green";
    const chtClass = chtSensor?.suspicion >= 24 ? "red" : chtSensor?.suspicion >= 10 ? "amber" : "green";
    return `
      <article class="engine-cylinder-detail">
        <header>
          <div>
            <p class="eyebrow">Cylinder Detail</p>
            <h3>${escapeHtml(info.label)}</h3>
          </div>
          <button class="secondary-action" data-cylinder-clear type="button">Show all cylinders</button>
        </header>
        <div class="engine-cylinder-detail-grid">
          <div>
            <span>CHT</span>
            <strong>${panelNumber(quantile(chtValues, 0.95), " F")}</strong>
            <em>${metric.chtNote}</em>
          </div>
          <div>
            <span>Cooling Rate</span>
            <strong class="${scoreClass(metric.coolingRateScore)}">${cylinderScoreMarkup(metric.coolingRateScore)}</strong>
            <em>${escapeHtml(metric.coolingRateNote)}</em>
          </div>
          <div>
            <span>EGT</span>
            <strong>${panelNumber(quantile(egtValues, 0.9), " F")}</strong>
            <em>or cooler for 90% of samples · peak ${panelNumber(extentFromValues(egtValues)?.max, " F")}</em>
          </div>
          ${metric.inductionLeakFlagged ? `
            <div>
              <span>Induction Leak</span>
              <strong class="${scoreClass(metric.inductionLeakScore)}">${cylinderScoreMarkup(metric.inductionLeakScore)}</strong>
              <em>${escapeHtml(metric.inductionLeakNote)}</em>
            </div>
          ` : ""}
          <div>
            <span>Peer Gap</span>
            <strong>${panelNumber(quantile(chtDeltaValues, 0.95), " F")}</strong>
            <em>CHT gap within engine for 95% · EGT gap ${panelNumber(quantile(egtDeltaValues.map(Math.abs), 0.9), " F")} or less for 90%</em>
          </div>
          <div>
            <span>Probe Confidence</span>
            <strong><b class="${egtClass}">EGT</b> <b class="${chtClass}">CHT</b></strong>
            <em>EGT ${escapeHtml(cylinderProbeNote(egtSensor))} · CHT ${escapeHtml(cylinderProbeNote(chtSensor))}</em>
          </div>
          <div>
            <span>EGT Rhythm</span>
            <strong class="${scoreClass(metric.valveScore)}">${cylinderScoreMarkup(metric.valveScore)}</strong>
            <em>${escapeHtml(metric.valveNote)}</em>
          </div>
        </div>
        <p>${metric.inductionLeakFlagged ? `This cylinder is the source of the induction-leak review cue. ` : ""}${metric.valveFlagged ? `This cylinder is the source of the EGT-rhythm review cue. ` : ""}Chart focus is set to ${escapeHtml(egtLabel)} and ${escapeHtml(chtLabel)}. Current scoring still uses the aircraft-level health model while this view scopes the cylinder evidence to ${escapeHtml(info.engine?.label ?? "the engine")}.</p>
      </article>
    `;
  }

  function renderCylinderDetailSection(cylinders, summary, engines = currentEngineDescriptors()) {
    if (!cylinders) return;
    const flightData = getFlightData();
    const rows = flightData.filter(engineWorkPoint);
    const analysisRows = rows.length >= 30 ? rows : flightData;
    const cylinderNumbers = engineScopeCylinderNumbers(analysisRows, engines);
    let selectedCylinderNumber = getSelectedCylinderNumber();
    if (selectedCylinderNumber && !cylinderNumbers.includes(selectedCylinderNumber)) {
      setSelectedCylinderNumber(null);
      selectedCylinderNumber = null;
    }
    if (!cylinderNumbers.length) {
      cylinders.innerHTML = '<p class="muted">No per-cylinder CHT or EGT channels were found in this log.</p>';
      return;
    }

    const sensorTop = summary.sensors?.details?.top ?? {};
    const sensorKey = summary.sensors?.score < 88 || sensorTop.suspicion >= 18 ? sensorTop.key ?? "" : "";
    const valveCylinder = summary.valve?.score < 88 ? summary.valve?.details?.top?.cylinder ?? null : null;
    const chtRhythmCylinders = summary.chtRhythm?.score < 88
      ? summary.chtRhythm?.details?.top?.cylinders ?? [summary.chtRhythm?.details?.top?.cylinder].filter(Number.isFinite)
      : [];
    const combustionCylinder = summary.combustion?.score < 88 ? summary.combustion?.details?.top?.cylinder ?? null : null;
    const divergenceCylinders = visibleDivergenceCylinders(summary.divergence);
    const magCylinder = summary.mag?.score < 88 ? summary.mag?.details?.top?.weakResponses?.[0]?.cylinder ?? null : null;
    const inductionLeakCylinder = summary.inductionLeak?.score < 88 ? summary.inductionLeak?.details?.top?.cylinder ?? null : null;
    const coolingCylinder = summary.cooling?.score < 88 ? summary.cooling?.details?.top?.cylinder ?? null : null;
    const coolingRateCylinder = summary.coolingRate?.score < 88 ? summary.coolingRate?.details?.top?.cylinder ?? null : null;
    const hottestCylinder = summary.cht?.score < 88 ? summary.cht?.details?.hottest?.cylinder ?? null : null;
    const egtCylinder = summary.egt?.score < 88 ? summary.egt?.details?.dominant?.cylinder ?? null : null;
    const chtKeys = cylinderNumbers.map((number) => `cht${number}`).filter((key) => analysisRows.some((point) => Number.isFinite(point[key])));
    const egtKeys = cylinderNumbers.map((number) => `egt${number}`).filter((key) => analysisRows.some((point) => Number.isFinite(point[key])));
    const averageCht = cylinderAverageForKeys(analysisRows, chtKeys);
    const averageChtSpread = cylinderSpreadAverageForKeys(analysisRows, chtKeys);
    const averageEgt = cylinderAverageForKeys(analysisRows, egtKeys);
    const averageEgtSpread = cylinderSpreadAverageForKeys(analysisRows, egtKeys);
    const averageMarkup = `
      <div class="engine-cylinder-averages" aria-label="Cylinder temperature averages">
        <div>
          <span>Avg CHT</span>
          <strong>${panelNumber(averageCht, " F")}</strong>
        </div>
        <div>
          <span>Avg CHT diff</span>
          <strong>${panelNumber(averageChtSpread, " F")}</strong>
          <small>Average high-low gap</small>
        </div>
        <div>
          <span>Avg EGT</span>
          <strong>${panelNumber(averageEgt, " F")}</strong>
        </div>
        <div>
          <span>Avg EGT diff</span>
          <strong>${panelNumber(averageEgtSpread, " F")}</strong>
          <small>Average high-low gap</small>
        </div>
      </div>
    `;

    const detail = selectedCylinderNumber ? cylinderDetailHtml(selectedCylinderNumber, analysisRows, summary, engines) : "";
    const numberSet = new Set(cylinderNumbers);
    const cylinderRowHtml = (number) => {
      const chtKey = `cht${number}`;
      const egtKey = `egt${number}`;
      const info = engineInfoForCylinderNumber(number, engines);
      const chtPeerKeys = enginePeerKeysForCylinderNumber(number, engines, "cht");
      const metric = cylinderMetricSummary(number, summary);
      const chtValues = finiteValues(analysisRows, chtKey);
      const egtValues = finiteValues(analysisRows, egtKey);
      const chtDeltaP95 = quantile(analysisRows.map((point) => {
        const value = point[chtKey];
        const peer = peerAverage(point, chtPeerKeys, chtKey);
        return Number.isFinite(value) && Number.isFinite(peer) ? value - peer : null;
      }), 0.95);
      const flags = [];
      if (sensorKey === chtKey || sensorKey === egtKey) flags.push("Probe signal");
      if (egtCylinder === number) flags.push("EGT spread source");
      if (valveCylinder === number) flags.push("EGT rhythm");
      if (chtRhythmCylinders.includes(number)) flags.push("CHT rhythm");
      if (combustionCylinder === number) flags.push("CHT/EGT pairing");
      if (divergenceCylinders.includes(number)) flags.push("Divergence");
      if (inductionLeakCylinder === number) flags.push("Induction leak");
      if (magCylinder === number) flags.push("Mag check");
      if (coolingCylinder === number) flags.push("Cooling");
      if (coolingRateCylinder === number) flags.push("Cooling rate");
      if (hottestCylinder === number) flags.push("Warmest CHT");
      const isFlagged = flags.some((flag) => ["Probe signal", "EGT spread source", "EGT rhythm", "CHT rhythm", "CHT/EGT pairing", "Divergence", "Mag check", "Cooling", "Cooling rate"].includes(flag));
      const isWatch = !isFlagged && flags.length;
      const isSelected = selectedCylinderNumber === number;
      const flagMarkup = flags.length
        ? `<div class="engine-cylinder-flags">${flags.map((flag) => `<span>${escapeHtml(flag)}</span>`).join("")}</div>`
        : '<span class="muted">No focused flags</span>';
      return `
        <tr class="${isFlagged ? "is-flagged" : isWatch ? "is-watch" : ""} ${isSelected ? "is-selected" : ""}" data-cylinder-row="${number}" tabindex="0">
          <td>
            <strong>${escapeHtml(info.label)}</strong>
            ${engines.length > 1 ? `<small>${escapeHtml(engineScopedProbeLabel(`egt${number}`, engines) || `EGT${number}`)} / ${escapeHtml(engineScopedProbeLabel(`cht${number}`, engines) || `CHT${number}`)}</small>` : ""}
          </td>
          <td>
            <span class="engine-cylinder-score ${metric.className}">${metric.score}</span>
            <small>${escapeHtml(metric.label)}</small>
          </td>
          <td>${panelNumber(extentFromValues(chtValues)?.max, " F")}</td>
          <td>${panelNumber(quantile(chtValues, 0.95), " F")}</td>
          <td>${signedPanelNumber(chtDeltaP95, " F")}</td>
          <td>${panelNumber(quantile(egtValues, 0.9), " F")}</td>
          <td>${flagMarkup}</td>
          <td><button class="secondary-action compact-action" data-cylinder-chart="${number}" type="button">View chart</button></td>
        </tr>
      `;
    };
    const cylinderRows = engines.length > 1
      ? engineScopeVisibleEngines(engines).map((engine) => {
        const engineNumbers = engineSourceNumbers(engine).filter((number) => numberSet.has(number));
        if (!engineNumbers.length) return "";
        return `
          <tr class="engine-cylinder-engine-row" data-engine-group="${escapeHtml(engine.id)}">
            <th colspan="8"><span>${escapeHtml(engine.shortLabel)}</span>${escapeHtml(engine.label)}</th>
          </tr>
          ${engineNumbers.map(cylinderRowHtml).join("")}
        `;
      }).join("")
      : cylinderNumbers.map(cylinderRowHtml).join("");
    cylinders.innerHTML = `
      ${detail}
      ${averageMarkup}
      <div class="engine-cylinder-table-wrap table-wrap">
        <table class="data-table engine-cylinder-table">
          <thead>
            <tr>
              <th>Cylinder</th>
              <th>Status</th>
              <th>CHT Max</th>
              <th>High CHT</th>
              <th>CHT vs peers</th>
              <th>High EGT</th>
              <th>Flags</th>
              <th>Chart</th>
            </tr>
          </thead>
          <tbody>${cylinderRows}</tbody>
        </table>
      </div>
    `;
    cylinders.querySelectorAll("[data-cylinder-row]").forEach((row) => {
      const activate = () => setCylinderFocus(Number(row.dataset.cylinderRow), { openOverview: false });
      row.addEventListener("click", activate);
      row.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        activate();
      });
    });
    cylinders.querySelectorAll("[data-cylinder-chart]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.stopPropagation();
        setCylinderFocus(Number(button.dataset.cylinderChart), { centerChart: true });
      });
    });
    cylinders.querySelector("[data-cylinder-clear]")?.addEventListener("click", clearCylinderFocus);
  }

  return {
    cylinderDetailHtml,
    renderCylinderDetailSection,
  };
}
